import * as React from "react";
import { Link } from "react-router-dom";
import { Card, Tag, Typography, Image } from "antd";
import {
  ImageDescription,
  IndexList,
  List,
  Marker,
  Note,
  ProgressCircle,
} from "../../common";

const list: List[] = [
  {
    title: "いろいろなアカウント攻撃の方法",
    children: [
      {
        title: "総当たり攻撃",
      },
      {
        title: "辞書攻撃",
      },
      {
        title: "リスト型攻撃",
        children: [{ title: "情報漏洩のチェック" }],
      },
    ],
  },
  {
    title: "安全なパスワードの設定と保管方法",
    children: [
      {
        title: "安全なパスワードの作り方",
        children: [
          { title: "パスワードの使い回し" },
          { title: "ソーシャルログイン" },
          { title: "パスワードの定期的な変更は必要か" },
          { title: "秘密の質問に答えない" },
        ],
      },
      {
        title: "安全にパスワードを保管する",
      },
    ],
  },
  {
    title: "ログイン方法をもっと安全に",
    children: [
      {
        title: "二要素認証",
      },
      {
        title: "二段階認証",
        children: [{ title: "多要素認証は安全か" }],
      },
    ],
  },
];

const { Title } = Typography;
export const AccountSecurity = () => {
  return (
    <div>
      <ProgressCircle />
      <IndexList list={list} />
      <Title level={3}>
        <span id="いろいろなアカウント攻撃の方法">
          １．いろいろなアカウント攻撃の方法
        </span>
      </Title>
      <Image
        src="https://4.bp.blogspot.com/-7zmw2S9L_Tc/VMItm5AFGaI/AAAAAAAAqv4/F0ocVz_Twjg/s550/sensou_senjou.png"
        preview={false}
      />
      <br />
      ウェブサイトやアプリを使うために必要なアカウント
      <Note
        title="アカウント"
        content={
          <>
            コンピュータやソフトウェア、ネットワークなどを使用するための権利や資格のこと。
            また、それらのシステムにログインするために必要なIDとパスワードの組み合わせをアカウントと呼ぶこともあります。
          </>
        }
      />
      ですが、インターネット利用者の増加とともにSNSの乗っ取りや個人情報漏洩などが問題になっています。
      ここでは実際に使われる攻撃方法と対策を学びます。
      <Title level={4}>
        <span id="総当たり攻撃">
          総当たり攻撃
          <Note
            title="総当たり攻撃"
            content="攻撃者が「ログインパスワード」や「暗号キー」を破るために、すべての文字などの組み合わせを試す攻撃。ブルートフォース攻撃とも呼ばれる。"
          />
        </span>
      </Title>
      名前の通り、「AAAAAA」「AAAAAB」のように考えられる組み合わせのパスワードを総当たりする方法です。
      英語表記のブルートフォース攻撃と呼ばれることもあります。
      多くのサービスは一定回数以上間違えると次のログインまで制限がかかったり、アカウントがロックされたり、データが消去されるようになっています。
      長いパスワードを使っていても時間をかければ成功するようにも見えますが、
      一文字増えるだけでも攻撃にかかる時間が大きく増えるので安全なパスワードを使えば防げる攻撃です。
      ちなみにアルファベットのみの6桁のパスワードなどは1週間もあれば総当たりに成功できるとよばれています。
      安全なパスワードは次の
      <a href="安全なパスワードの作り方">安全なパスワードの作り方</a>
      で確認します。また、不正なログインが観測されたらすぐにメールなどが届くように設定したり、ログイン履歴を確認するのも役に立ちます。
      愛知県に住んでいるのに東京やブラジルなどの自分ではありえない場所からログイン試行が見られた場合には、
      パスワードの変更を含めたセキュリティ対策をとることが必要になります。
      <Marker>
        対策には「記号などを使った複雑で長いパスワード」「ログイン履歴から早期発見」が有効です。
      </Marker>
      <br />
      <br />
      総当たり攻撃の中でもログイン試行回数を制限されるのを回避するリバースブルートフォース攻撃と呼ばれる攻撃があります。
      これは一般的な総当たり攻撃が一つのアカウントに対していくつものパスワードを入力するのに対して、
      リバースブルートフォース攻撃は一つのパスワードをいくつものアカウントに入力します。
      ログインは一度だけですから、攻撃者がログイン試行回数の制限を受けることはありません。
      これに対しても複雑なパスワードやログイン履歴の確認が大事です。
      <Title level={4}>
        <span id="辞書攻撃">
          辞書攻撃
          <Note
            title="辞書攻撃"
            content="「ログインパスワード」などによく使われる文字列を集めて辞書化したものを使い、不正に他人のアカウントにログインできないかを試みる攻撃。"
          />
        </span>
      </Title>
      <Image
        src="https://1.bp.blogspot.com/-BYOigKcBTGY/VkxLkKfHwWI/AAAAAAAA0n0/7Mb5mGxI6L8/s800/dictionary.png"
        preview={false}
      />
      <br />
      辞書攻撃はよく使われるパスワードを集めて辞書化したものを使う方法です。
      <a
        href="https://nordpass.com/most-common-passwords-list/"
        target="_blank"
        rel="noreferrer"
      >
        NordPass
      </a>
      でよく使われる200のパスワードが公開されています。
      2020年になっても「123456」のような最弱のパスワードを使用しているユーザーが多いのは非常に残念ですが、
      リスト一覧を見ると「数字の羅列」「意味のある単語」「キーボードにある文字の羅列」に危険が潜んでいることが分かります。
      <Marker>対策には「意味のないランダムなパスワード」が有効です。</Marker>
      <Title level={4}>
        <span id="リスト型攻撃">
          リスト型攻撃
          <Note
            title="リスト型攻撃"
            content="ウェブサービスなどから流出したパスワードのリストなどを使って、ほかのサービスでログインを試みる攻撃。
            パスワードリスト攻撃やアカウントリスト攻撃とも呼ばれる。"
          />
        </span>
      </Title>
      <Image
        src="https://1.bp.blogspot.com/-r7LqBL2a1GA/XaKa2g3kWXI/AAAAAAABVjY/tryXn3QOacc1U9WGpKPjsggNRsIyUEhHwCNcBGAsYHQ/s400/document_list.png"
        preview={false}
      />
      <br />
      リスト型攻撃とは今までに流出したアカウントやパスワードなどのデータを使用して、ほかのサービスにログインする方法です。
      <a href="https://haveibeenpwned.com" target="_blank" rel="noreferrer">
        Have I been pwned
      </a>
      によると、今までになんと六億個以上のパスワードが流出しています。
      以下のコラムで自分のメールアドレスやパスワードが流出していないかを確認することができます。
      <Marker>対策には「パスワードを使い回さないこと」が有効です。</Marker>
      <Card>
        <Title level={4}>
          <span id="情報漏洩のチェック">
            情報漏洩のチェック！ <Tag color="blue">今すぐ実践</Tag>
          </span>
        </Title>
        内閣サイバーセキュリティセンター（NISC）からも提案されている
        <a href="https://haveibeenpwned.com" target="_blank" rel="noreferrer">
          Have I been pwned
        </a>
        （運営：マイクロソフト社／トロイ・ハント）
        では、今までインターネット上に流出したメールアドレスやパスワード情報を収集しデータベース化しされているので自分のアカウントが今まで被害にあったことがあるかを確認できます。
        <ul>
          <li>
            <a
              href="https://haveibeenpwned.com"
              target="_blank"
              rel="noreferrer"
            >
              メールアドレス流出の確認
            </a>
          </li>
          <li>
            <a
              href="https://haveibeenpwned.com/Passwords"
              target="_blank"
              rel="noreferrer"
            >
              パスワード流出の確認
            </a>
          </li>
        </ul>
        もし流出していることが発覚したら
        <Marker>速やかに対象となるアカウント情報を変更してください。</Marker>
      </Card>
      <Title level={3}>
        <span id="安全なパスワードの設定と保管方法">
          ２．安全なパスワードの設定と保管方法
        </span>
      </Title>
      ほとんどのウェブサイトでアカウント認証に使われるパスワードですが、安全なパスワードによって多くの攻撃を防ぐことができます。
      自分を証明するためのものですから人に教えないことはもちろんですが、周りから推測されない安全なパスワードであることが大切です。
      <br />
      <br />
      <Title level={4}>
        <span id="安全なパスワードの作り方">安全なパスワードの作り方</span>
      </Title>
      <Image
        src="https://4.bp.blogspot.com/-eyxlfj6JmIU/UbVvN09MvZI/AAAAAAAAUsE/Bp_I-GAJHnI/s400/computer_password.png"
        preview={false}
      />
      <br />
      <a href="https://www.soumu.go.jp/main_sosiki/joho_tsusin/security/business/staff/01.html">
        総務相
      </a>
      による安全なパスワードの定義は以下の通りです。
      <ol>
        <li>名前などの個人情報からは推測できないこと</li>
        <li>英単語などをそのまま使用していないこと</li>
        <li>アルファベットと数字が混在していること</li>
        <li>適切な長さの文字列であること</li>
        <li>類推しやすい並び方やその安易な組合せにしないこと</li>
      </ol>
      ここで一番大事なのは<Marker>「他人が推測できないもの」</Marker>
      にすることです。また日本語と英語の単語を混ぜたりすることもいい戦法です。
      また、アルファベット／数字／記号のすべてを必須とするパスワードも多く見られますが、
      未だに人気パスワードリストの上位には 「Password123!」のような
      条件は満たしているものの安全ではないもの も見られるので、
      今一度自分のパスワードの安全性を考えてみましょう。
      <br />
      <br />
      また、内閣サイバーセキュリティセンター（NISC）では
      <Marker>
        英大文字小文字＋数字＋記号で 10桁以上のパスワードを推奨しています。
      </Marker>
      10桁の数字のみだと100億通りですが、すべての種類を使うことで同じ桁数でも3000京通りになります。
      この時点で総当たり攻撃は時間がかかりすぎるので事実上不可能な状態になります。
      あくまで10桁が安全圏というだけで、桁数を増やすことでさらに安全なパスワードを作ることができます。
      <Title level={5}>
        <span id="パスワードの使い回し">パスワードの使い回し</span>
      </Title>
      少し繰り返しにもなりますが、パスワードの使い回しはとても危険です。
      もし登録しているサイトが20個あれば、単純に情報漏洩のリスクが20倍になるわけです。
      また<a href="#リスト型攻撃">リスト型攻撃</a>
      という攻撃方法が存在している以上、
      パスワードはサイトによって全く違うものを使用する必要があります。
      <Title level={5}>
        <span id="ソーシャルログイン">ソーシャルログイン</span>
      </Title>
      最近は「Twitterでログイン」「Facebookでログイン」などの堅牢なサービスのアカウントを別のサービスにもそのまま使うソーシャルログインというログイン方法を見かけるようになりました。
      使われるのは世界的にも大手の堅牢なサービスで記録するパスワードが増えないので便利ではありますが、
      大元のアカウント認証情報が漏れたときに芋づる式に他のサービスまでアクセスされてしまう危険があります。
      内閣サイバーセキュリティセンターでは個別のパスワードを使用することが推奨されています。
      <Card>
        <Title level={5}>
          <span id="パスワードの定期的な変更は必要か">
            パスワードの定期的な変更は必要か
          </span>
        </Title>
        長くインターネットに触れてきたユーザーなら一度は定期的なパスワードの変更を求められたことがあるかもしれません。
        <a href="https://pages.nist.gov/800-63-3/sp800-63b.html">
          米国国立標準技術研究所（NIST）
        </a>
        は定期的なパスワードの変更を推奨してきましたが、
        2017年以降、パスワードの定期的な変更を促すことでユーザーがパターン化した簡単なパスワードを作成するようになってしまったため、
        <Marker>推測不可能でより強力な固有のパスワード</Marker>
        を推奨しています。
      </Card>
      <Title level={5}>
        <span id="秘密の質問に答えない">秘密の質問に答えない</span>
      </Title>
      アカウントを登録するときに、パスワードの後に聞かれたりする秘密の質問。
      メールアドレス（またはユーザー名）と秘密の質問の答えさえ知っていればパスワードをリセットすることもできてしまいます。
      特にこの秘密の質問はは「生まれた市は」や「ペットの犬の名前は」のようなSNSが普及した現在では秘密とは呼べない個人情報が多く、
      まったく会ったこともない人間でも推測することが可能になっています。もちろん秘密の質問の答えも使い回さないようにしましょう。
      <ImageDescription
        src="https://i.imgur.com/2IzcvFW.png"
        description={
          <>
            引用：
            <a
              href="https://www.nisc.go.jp/security-site/files/handbook-all.pdf"
              target="_blank"
              rel="noreferrer"
            >
              内閣サイバーセキュリティセンター／インターネットの安全・安心ハンドブック／32ページ
            </a>
          </>
        }
      />
      <Title level={4}>
        <span id="安全にパスワードを保管する">安全にパスワードを保管する</span>
      </Title>
      パスワードは頭の中に入れている人が多いかもしれませんが、適切な条件の下であれば保管する方法もあります。
      簡単に覚えられるパスワードよりも自分で覚えられないぐらい複雑なパスワードのほうが優位です。
      <ol>
        <li>パスワードを使用する場所に置かない（ログインする端末を含む）</li>
        <li>ブラウザの自動入力機能は使わない</li>
        <li>紙のパスワード管理ノートを使う</li>
        <li>パスワード管理アプリを使う</li>
      </ol>
      パソコンやスマホにパスワードをテキストで保管するのはとても危険です。
      マルウェア感染時に簡単にすべてのパスワードが流出する恐れだけでなく、そもそも本体ごと盗まれる可能性があるからです。
      紙のパスワード管理ノートを作るときもパソコンやスマホと別の場所に厳重に保管する必要があります。
      <br />
      <br />
      またブラウザの自動入力機能はパソコンやスマホがロックされていない状態であれば
      誰でも自由にログインできるので最初からパスワードが存在しないのと同じぐらい危険です。
      パスワード管理アプリを使う場合はできる限りクラウド保管するものを避けるか、
      クラウド上にデータを保存する前に「アプリ名　トラブル」などで検索して安全を確認してから使用してください。
      最後に忘れがちですが、ほとんどのパスワード管理アプリはPINコードや指紋認証、暗号化などで情報が守られていますが、
      管理しているパスワードのバックアップも忘れないようにしましょう。もちろん暗号化は欠かせません。
      <Title level={3}>
        <span id="ログイン方法をもっと安全に">
          ３．ログイン方法をもっと安全に
        </span>
      </Title>
      本当に複雑なパスワードだけでもセキュリティ対策が万全とは言えません。
      ここではパスワード以外にも認証をさらに安全にする方法を学びます。
      <Title level={4}>
        <span id="二要素認証">二要素認証</span>
      </Title>
      <Image
        src="https://4.bp.blogspot.com/-HiGln9A3BIo/V8VFGtK-zmI/AAAAAAAA9Zo/oyGGN-VifzofbVCSiUO3_eQE7B_5aA4hgCLcB/s400/smartphone_shimon_ninsyou.png"
        preview={false}
      />
      <br />
      インターネット上でその人を認識するためには大きく3つの要素に分かれています。
      <ol>
        <li>
          <b>知識（知っているもの）</b>
          <ul>
            <li>パスワード</li>
            <li>PINコード</li>
            <li>秘密の質問</li>
          </ul>
        </li>
        <br />
        <li>
          <b>所有物（持っているもの）</b>
          <ul>
            <li>カード</li>
            <li>スマホ</li>
            <li>セキュリティキー</li>
          </ul>
        </li>
        <br />
        <li>
          <b>生体（本人自身）</b>
          <ul>
            <li>指紋</li>
            <li>虹彩</li>
            <li>顔</li>
          </ul>
        </li>
      </ol>
      これらのうち２つ使うものを二要素認証、それ以上のものを多要素認証と言います。
      銀行のキャッシュカードを使用するときには暗証番号（１）とキャッシュカード（２）を使用しているので二要素認証です。
      ログインするときに特定のスマホ（２）から暗証番号（１）と指紋認証（３）が必要なサービスは多要素認証を使っています。
      逆につけば、パスワード（１）とPINコード（１）の両方を求めていても要素は「知識」のひとつだけなので二要素認証にはなりません。
      アカウントの乗っ取りなどを避けるには最低でも二要素認証が推奨されています。
      しかし、
      <Marker>
        ＳＭＳ（ショートメッセージ）を使ったワンタイムパスワード送信はＳＩＭハイジャックという事例があるため、
        パスワードを忘れた場合でもＳＭＳを使った認証は避ける
      </Marker>
      ようにしましょう。
      <Title level={4}>
        <span id="二段階認証">
          二段階認証
          <Note
            title="二段階認証"
            content="利用者認証を２回に分けて行うもの。多要素認証と異なり、同じ認証の要素で２つの段階に分けて認証する場合もそう呼ぶ。
            一方、異なる要素を組み合わせて２回認証を行う場合は二要素認証とも呼ぶ。同じ要素２回よりは異なる要素２回の方がセキュリティレベルは高くなる。"
          />
        </span>
      </Title>
      <Image
        src="https://4.bp.blogspot.com/-J7X7rh36Vl4/WnuyI3K44kI/AAAAAAABKAU/9xQ74K-o9JwYrTQ3MIvYTMooJU3L7SU2ACLcBGAs/s800/internet_nidankai_ninsyou_simple.png"
        preview={false}
      />
      <br />
      二段階認証は要素にかかわらず認証のプロセスが二段階に分かれているものです。
      ユーザー名とパスワードをログインした後にワンタイムパスワード（OTP）
      <Note
        title="ワンタイムパスワード"
        content="二段階認証などで用いられる、利用するたびに更新されるパスワード。使い捨てパスワード。"
      />
      を求めるものが多く、
      その場合は「知識」「所有物」の２つの要素が問われるので二要素認証でもあります。
      先ほど述べたように既に攻撃が見つかっているＳＭＳ（ショートメッセージ）を使ったワンタイムパスワードは避けるようにしましょう。
      またウェブサービスへのログインは必ず多要素認証を使用してください。
      <Card>
        <Title level={5}>
          <span id="多要素認証は完全か">多要素認証は完全か</span>
        </Title>
        二段階認証（二要素認証）で使われるワンタイムパスワードは一定時間ごとに変わるため、従来のパスワード攻撃では理論上破らることが不可能です。
        どのセキュリティエキスパートに聞いても多要素認証は使用すべきセキュリティ対策というでしょう。
        <ImageDescription
          src="https://i.imgur.com/z8Tz22W.png"
          description={
            <>
              引用：
              <a
                href="https://www.nisc.go.jp/security-site/files/handbook-all.pdf"
                target="_blank"
                rel="noreferrer"
              >
                内閣サイバーセキュリティセンター／インターネットの安全・安心ハンドブック／77ページ
              </a>
            </>
          }
        />
        しかし中間者攻撃は多要素認証すら破ることができます。
        ログインパスワードとワンタイムパスワードの二要素認証を必要とするインターネット銀行を使うときに、
        もしウェブサイトが偽物だったらどうなりますか。
        攻撃者が、私たちが偽サイトに入力した内容を 本物のサイトに中継して、
        画面の内容をリアルタイムに模倣してい
        たとしても、気付かないまま送金の操作をしてしまうでしょう。
        これを防ぐためにはEV-SSL証明書を確認しましょう（
        <Link to="/lectures/device-security#さらに安全なEV-SSL証明書の登場">
          デバイスセキュリティーさらに安全なEV-SSL証明書の登場
        </Link>
        ）
      </Card>
    </div>
  );
};

export default AccountSecurity;
